/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from antd/components/message/index.tsx
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import classNames from 'classnames';
import RCNotification from 'rc-notification';
import ConfigProvider, { globalConfig } from '../config-provider';
import { getKeyThenIncreaseKey, ThenableArgument, MessageType, attachTypeApi, MessageInstance, MessageApi, ConfigOnClose, getInstance } from 'antd/lib/message/index';
import createUseMessage from 'antd/lib/message/hooks/useMessage';
import { Icon } from '../icon';
import { Loading } from '../loading';
export { getKeyThenIncreaseKey, attachTypeApi };
/** @private test Only function. Not work on production */
export { getInstance };
let messageInstance;
let defaultDuration = 3;
let defaultTop;
let key = 1;
let localPrefixCls = '';
let transitionName = 'move-up';
let hasTransitionName = false;
let getContainer;
let maxCount;
let rtl = false;
let defaultCloseIcon;
function setMessageConfig(options) {
    if (options.top !== undefined) {
        defaultTop = options.top;
        messageInstance = null; // delete messageInstance for new defaultTop
    }
    if (options.duration !== undefined) {
        defaultDuration = options.duration;
    }
    if (options.prefixCls !== undefined) {
        localPrefixCls = options.prefixCls;
    }
    if (options.getContainer !== undefined) {
        getContainer = options.getContainer;
    }
    if (options.transitionName !== undefined) {
        transitionName = options.transitionName;
        messageInstance = null; // delete messageInstance for new transitionName
        hasTransitionName = true;
    }
    if (options.maxCount !== undefined) {
        maxCount = options.maxCount;
        messageInstance = null;
    }
    if (options.rtl !== undefined) {
        rtl = options.rtl;
    }
    if (options.closeIcon !== undefined) {
        defaultCloseIcon = options.closeIcon;
    }
}
function getRCNotificationInstance(args, callback) {
    const { prefixCls: customizePrefixCls } = args;
    const { getPrefixCls, getRootPrefixCls, getIconPrefixCls } = globalConfig();
    const prefixCls = getPrefixCls('message', customizePrefixCls || localPrefixCls);
    const rootPrefixCls = getRootPrefixCls(args.rootPrefixCls, prefixCls);
    const iconPrefixCls = getIconPrefixCls();
    if (messageInstance) {
        callback({ prefixCls, rootPrefixCls, iconPrefixCls, instance: messageInstance });
        return;
    }
    const instanceConfig = {
        prefixCls,
        transitionName: hasTransitionName ? transitionName : `${rootPrefixCls}-${transitionName}`,
        style: { top: defaultTop },
        getContainer,
        maxCount
    };
    RCNotification.newInstance(instanceConfig, (instance) => {
        if (messageInstance) {
            callback({ prefixCls, rootPrefixCls, iconPrefixCls, instance: messageInstance });
            return;
        }
        messageInstance = instance;
        if (process.env.NODE_ENV === 'test') {
            messageInstance.config = instanceConfig;
        }
        callback({ prefixCls, rootPrefixCls, iconPrefixCls, instance });
    });
}
const typeToIcon = {
    info: React.createElement(Icon, { type: "information--filled" }),
    success: React.createElement(Icon, { type: 'checkmark--filled' }),
    error: React.createElement(Icon, { type: 'misuse--filled' }),
    warning: React.createElement(Icon, { type: 'warning--filled' }),
    loading: React.createElement(Loading, { size: 'default' })
};
const typeToTitle = {
    info: '提示信息',
    success: '成功提醒',
    error: '错误提醒',
    warning: '警告提醒',
    loading: '正在加载'
};
function getRCNoticeProps(args, prefixCls, iconPrefixCls) {
    const { title = false } = args;
    const duration = args.duration !== undefined ? args.duration : defaultDuration;
    const iconComponent = typeToIcon[args.type];
    const newTitle = title && typeof title !== 'boolean' ? title : typeToTitle[args.type];
    const closeIcon = defaultCloseIcon;
    const closeIconToRender = (React.createElement("span", { className: `${prefixCls}-close-x` }, closeIcon || React.createElement(Icon, { type: 'close', className: `${prefixCls}-close-icon`, size: 'small' })));
    const newCloseable = args.closeable === false && duration !== 0 ? false : true;
    const messageNoticeClass = classNames({
        [`${prefixCls}-notice-${args.type}`]: args.type,
        [`${prefixCls}-notice-with-background`]: !!args.withBackground,
        [`${prefixCls}-notice-large`]: args.size === 'large',
        [`${prefixCls}-glassmorphism`]: true
    }, args.className);
    const messageClass = classNames(`${prefixCls}-custom-content`, {
        [`${prefixCls}-${args.type}`]: args.type,
        [`${prefixCls}-rtl`]: rtl === true
    });
    return {
        key: args.key,
        duration,
        style: args.style || {},
        closable: newCloseable,
        closeIcon: closeIconToRender,
        className: messageNoticeClass,
        content: (React.createElement(ConfigProvider, { iconPrefixCls: iconPrefixCls },
            React.createElement("div", { className: messageClass },
                args.icon || iconComponent,
                title !== false && (React.createElement("span", { className: `${prefixCls}-title ${prefixCls}-${args.type}-title` }, newTitle)),
                React.createElement("span", null, args.content)))),
        onClose: args.onClose,
        onClick: args.onClick
    };
}
function notice(args) {
    const target = args.key || key++;
    const closePromise = new Promise((resolve) => {
        const callback = () => {
            if (typeof args.onClose === 'function') {
                args.onClose();
            }
            return resolve(true);
        };
        getRCNotificationInstance(args, ({ prefixCls, iconPrefixCls, instance }) => {
            instance.notice(getRCNoticeProps({ ...args, key: target, onClose: callback }, prefixCls, iconPrefixCls));
        });
    });
    const result = () => {
        if (messageInstance) {
            messageInstance.removeNotice(target);
        }
    };
    result.then = (filled, rejected) => closePromise.then(filled, rejected);
    result.promise = closePromise;
    return result;
}
const api = {
    open: notice,
    config: setMessageConfig,
    destroy(messageKey) {
        if (messageInstance) {
            if (messageKey) {
                const { removeNotice } = messageInstance;
                removeNotice(messageKey);
            }
            else {
                const { destroy } = messageInstance;
                destroy();
                messageInstance = null;
            }
        }
    }
};
['success', 'info', 'warning', 'error', 'loading'].forEach((type) => attachTypeApi(api, type));
api.warn = api.warning;
api.useMessage = createUseMessage(getRCNotificationInstance, getRCNoticeProps);
export { api as message };
export default api;
